var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { normalizeRoute, normalizeUrl, paramsSerializer, parseAxiosError, getServiceUrlWithRoute } from './utils';
import { DISCOVERY_ROUTE, DISCOVERY_ROUTE_ACCOUNTID, DISCOVERY_ROUTE_PROVIDERS_ACCOUNT_ID, TIMEOUT_SIGNAL_REASON, SdkStatus, } from './constants';
import { ApiTimeoutError } from './errors';
export class Base {
    constructor({ token, discoveryUrl, serviceKey, requestAccountId, requestProvidersAccountId, feature, accountId, serviceUrl, useDefaultSerializer, paramSerializer = paramsSerializer, httpAgents = {}, interceptors = {}, }) {
        this.discoveryUrl = null;
        this.requestAccountId = false;
        this.requestProvidersAccountId = false;
        this._isCrossAccount = false;
        this.status = SdkStatus.NEW;
        this._currentAccountId = null;
        this._targetAccountId = null;
        this._providersAccountId = null;
        this._serviceUrl = null;
        this.token = token;
        this.discoveryUrl = discoveryUrl || null;
        this.serviceKey = serviceKey;
        this.axios = axios.create(Object.assign(Object.assign({}, (useDefaultSerializer ? {} : {
            paramsSerializer: { serialize: paramSerializer },
        })), httpAgents));
        this._serviceUrl = serviceUrl || null;
        this.injectInterceptors(interceptors);
        if (accountId) {
            this._currentAccountId = accountId;
            this._targetAccountId = accountId;
            this._isCrossAccount = true;
        }
        else {
            this.requestAccountId = requestAccountId || false;
        }
        this.requestProvidersAccountId = requestProvidersAccountId || false;
        this.feature = feature || null;
    }
    /**
     * @deprecated used for migration stage only
     * @ignore
     */
    get currentAccountId() {
        return this._currentAccountId;
    }
    /** @ignore */
    get targetAccountId() {
        return this._targetAccountId;
    }
    /**
     * @deprecated used for migration stage only
     * @ignore
     */
    get providersAccountId() {
        return this._providersAccountId;
    }
    /** @ignore */
    get isCrossAccount() {
        return this._isCrossAccount;
    }
    /** @ignore */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.serviceUrl) {
                    this._serviceUrl = this.makeApiUrl({ url: normalizeUrl(this.serviceUrl) });
                }
                else if (this.discoveryUrl) {
                    const [data, accountId, providersAccountId,] = yield Promise.all([
                        this.getServiceData(),
                        this.requestAccountId ? this.getAccountId() : null,
                        this.requestProvidersAccountId ? this.getProvidersAccId() : null,
                    ]);
                    if (this.requestAccountId)
                        this._currentAccountId = accountId;
                    if (this.requestProvidersAccountId)
                        this._providersAccountId = providersAccountId;
                    this._serviceUrl = this.makeApiUrl(Object.assign(Object.assign({}, data), (accountId ? { accountId } : {})));
                }
                else {
                    throw new Error(`Url for '${this.serviceKey}' or 'discoveryUrl' in missing`);
                }
                this.status = SdkStatus.SUCCESS;
            }
            catch (e) {
                this.status = SdkStatus.ERROR;
                throw e;
            }
        });
    }
    injectInterceptors({ request, response }) {
        if (request) {
            const requestInterceptors = Array.isArray(request) ? request : [request];
            requestInterceptors.forEach((i) => this.axios.interceptors.request.use(i));
        }
        if (response) {
            const responseInterceptors = Array.isArray(response) ? response : [response];
            responseInterceptors.forEach((i) => this.axios.interceptors.response.use(i));
        }
    }
    /** @ignore */
    getServiceData() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.axios({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                url: `${normalizeUrl(this.discoveryUrl)}/${this.normalizeRoute(DISCOVERY_ROUTE)}`,
                params: Object.assign({ serviceName: this.serviceKey }, (this.feature ? { feature: this.feature } : {})),
            });
            return data;
        });
    }
    /** @ignore */
    getAccountId() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { accountId } } = yield this.axios({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                url: `${normalizeUrl(this.discoveryUrl)}/${this.normalizeRoute(DISCOVERY_ROUTE_ACCOUNTID)}`,
                headers: this.getHeaders(),
            });
            return accountId;
        });
    }
    /** @ignore */
    getProvidersAccId() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { providersAccountId } } = yield this.axios({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                url: `${normalizeUrl(this.discoveryUrl)}/${this.normalizeRoute(DISCOVERY_ROUTE_PROVIDERS_ACCOUNT_ID)}`,
                headers: this.getHeaders(),
            });
            return providersAccountId;
        });
    }
    /** @ignore */
    get serviceUrl() {
        return this._serviceUrl;
    }
    /**
     *
     * Override this method in case if api url has specific url arguments
     * @ignore
     */
    makeApiUrl(data) {
        return data.url;
    }
    /**
     * Method for parsing errors thrown from callApi request
     *
     * Override this method in case if you need specific error handling
     * @ignore
     */
    parseError(e) {
        return parseAxiosError(e);
    }
    /** @ignore */
    getHeaders() {
        return Object.assign({ 'Content-Type': 'application/json;charset=UTF-8' }, this.getToken() ? { Authorization: this.getToken() } : {});
    }
    /** @ignore */
    getToken() {
        if (typeof this.token === 'string') {
            return this.token;
        }
        if (typeof this.token === 'function') {
            return this.token();
        }
        throw new Error('token is not defined');
    }
    /**
     * Make API request
     * ```typescript
     * const result = await instance.callApi({
     *   route: '/route',
     *   method: 'GET',
     *   data: requestData,
     *   params: queryParams,
     *   timeout: 2500,
     * });
     * ```
     * @deprecated use callApiV2
     * @ignore
     */
    callApi(params) {
        return __awaiter(this, void 0, void 0, function* () {
            let timeoutTimer;
            if (this.status === SdkStatus.NEW) {
                yield this.init();
            }
            try {
                let timeoutController;
                let signal = params.signal;
                if (params.timeout && !params.signal) {
                    timeoutController = new AbortController();
                    signal = timeoutController.signal;
                }
                const conf = {
                    url: this.getUrl(params),
                    method: params.method || 'GET',
                    data: params.data,
                    params: params.params,
                    headers: Object.assign(Object.assign({}, this.getHeaders()), params.customHeaders),
                    signal,
                    withCredentials: params.withCredentials,
                    timeout: params.timeout,
                };
                if (params.timeout && timeoutController) {
                    timeoutTimer = setTimeout(() => timeoutController === null || timeoutController === void 0 ? void 0 : timeoutController.abort(new ApiTimeoutError(TIMEOUT_SIGNAL_REASON)), params.timeout);
                }
                const { data } = yield this.axios(conf);
                return data;
            }
            catch (e) {
                throw this.parseError(e);
            }
            finally {
                if (timeoutTimer)
                    clearTimeout(timeoutTimer);
            }
        });
    }
    /**
     * Make API request
     * ```typescript
     * const result = await instance.callApi({
     *   route: '/route',
     *   method: 'GET',
     *   data: requestData,
     *   params: queryParams,
     *   timeout: 2500,
     * });
     * ```
     * @ignore
     */
    callApiV2(params) {
        return __awaiter(this, void 0, void 0, function* () {
            let timeoutTimer;
            if (this.status === SdkStatus.NEW) {
                yield this.init();
            }
            try {
                let timeoutController;
                let signal = params.signal;
                if (params.timeout && !params.signal) {
                    timeoutController = new AbortController();
                    signal = timeoutController.signal;
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const query = params.params || {};
                if (this.isCrossAccount) {
                    query.accountId = this.targetAccountId;
                }
                const axiosConfig = {
                    url: this.getUrl(params),
                    method: params.method || 'GET',
                    data: params.data,
                    params: query,
                    headers: Object.assign(Object.assign({}, this.getHeaders()), params.customHeaders),
                    signal,
                    withCredentials: params.withCredentials,
                    timeout: params.timeout,
                };
                if (params.timeout && timeoutController) {
                    timeoutTimer = setTimeout(() => timeoutController === null || timeoutController === void 0 ? void 0 : timeoutController.abort(new ApiTimeoutError(TIMEOUT_SIGNAL_REASON)), params.timeout);
                }
                const { data } = yield this.axios(axiosConfig);
                return data;
            }
            catch (error) {
                throw this.parseError(error);
            }
            finally {
                if (timeoutTimer)
                    clearTimeout(timeoutTimer);
            }
        });
    }
    /**
     * Normalize route ro remove spaces from param
     * ```typescript
     * const result = await instance.normalizeRoute('/route');
     * ```
     * @ignore
     */
    normalizeRoute(data) {
        return normalizeRoute(data);
    }
    /**
     * Normalize URL for API request
     * @param {CalApiParams} params request params
     * @returns {string} normalized URL
     * @ignore
     */
    getUrl(params) {
        if (params.url)
            return params.url;
        if (!this.serviceUrl)
            throw new Error('serviceUrl is not defined or not discovered');
        return getServiceUrlWithRoute(this.serviceUrl, this.normalizeRoute(params.route));
    }
}
//# sourceMappingURL=Base.js.map